import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Card from '@mui/material/Card';
import MDBox from '../MDBox';
import MDTypography from '../MDTypography';
import config from '../../config';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Predefined California cities with coordinates
const CALIFORNIA_CITIES = [
  { name: 'Los Angeles', location: 'Los Angeles,CA', lat: 34.0522, lng: -118.2437 },
  { name: 'San Diego', location: 'San Diego,CA', lat: 32.7157, lng: -117.1611 },
  { name: 'San Francisco', location: 'San Francisco,CA', lat: 37.7749, lng: -122.4194 },
  { name: 'Sacramento', location: 'Sacramento,CA', lat: 38.5816, lng: -121.4944 },
  { name: 'Fresno', location: 'Fresno,CA', lat: 36.7378, lng: -119.7871 },
  { name: 'San Jose', location: 'San Jose,CA', lat: 37.3382, lng: -121.8863 },
  { name: 'Santa Barbara', location: 'Santa Barbara,CA', lat: 34.4208, lng: -119.6982 },
  { name: 'Redding', location: 'Redding,CA', lat: 40.5865, lng: -122.3917 },
  { name: 'Bakersfield', location: 'Bakersfield,CA', lat: 35.3733, lng: -119.0187 },
  { name: 'Palm Springs', location: 'Palm Springs,CA', lat: 33.8303, lng: -116.5453 },
  { name: 'Eureka', location: 'Eureka,CA', lat: 40.8021, lng: -124.1637 },
  { name: 'Monterey', location: 'Monterey,CA', lat: 36.6002, lng: -121.8947 },
];

// Create custom colored markers based on risk category
const createColoredIcon = (riskCategory) => {
  let color;
  switch (riskCategory) {
    case 'Low':
      color = '#2196F3'; // blue
      break;
    case 'Moderate':
      color = '#FFC107'; // yellow
      break;
    case 'High':
      color = '#FF9800'; // orange
      break;
    case 'Extreme':
      color = '#F44336'; // red
      break;
    default:
      color = '#9E9E9E'; // gray
  }

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
      "></div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

function WildfireRiskMap() {
  const [mapCityData, setMapCityData] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState('');

  // Fetch wildfire data for all predefined cities
  useEffect(() => {
    const fetchAllCityData = async () => {
      setMapLoading(true);
      setMapError('');

      try {
        const fetchPromises = CALIFORNIA_CITIES.map(async (city) => {
          try {
            const url = `${config.apiBaseUrl}/api/predict-wildfire-from-weather?location=${encodeURIComponent(city.location)}`;
            const response = await fetch(url);

            if (!response.ok) {
              console.warn(`Failed to fetch data for ${city.name}`);
              return null;
            }

            const data = await response.json();
            return {
              ...city,
              data: data,
            };
          } catch (error) {
            console.warn(`Error fetching data for ${city.name}:`, error);
            return null;
          }
        });

        const results = await Promise.all(fetchPromises);
        const validResults = results.filter((result) => result !== null);

        if (validResults.length === 0) {
          setMapError('Failed to fetch wildfire data for all cities');
        } else {
          setMapCityData(validResults);
        }
      } catch (error) {
        console.error('Error fetching city data:', error);
        setMapError('Failed to load wildfire risk map data');
      } finally {
        setMapLoading(false);
      }
    };

    fetchAllCityData();
  }, []);

  return (
    <MDBox mt={6} mb={3}>
      <MDTypography variant="h4" fontWeight="medium" color="info" textTransform="uppercase" textAlign="left" mb={1}>
        California Wildfire Risk Map
      </MDTypography>
      <MDTypography variant="body2" color="text" textAlign="left" mb={3}>
        Live wildfire danger estimates for selected California cities
      </MDTypography>

      <Card>
        <MDBox p={3}>
          {mapLoading && (
            <MDBox textAlign="center" py={5}>
              <MDTypography variant="h6" fontWeight="medium">
                Loading wildfire risk data for California cities...
              </MDTypography>
            </MDBox>
          )}

          {mapError && (
            <MDBox textAlign="center" py={5}>
              <MDTypography variant="body2" color="error">
                {mapError}
              </MDTypography>
            </MDBox>
          )}

          {!mapLoading && !mapError && mapCityData.length > 0 && (
            <>
              <MapContainer
                center={[36.7783, -119.4179]}
                zoom={6}
                style={{ height: '600px', width: '100%', borderRadius: '8px' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {mapCityData.map((cityData, index) => (
                  <Marker
                    key={index}
                    position={[cityData.lat, cityData.lng]}
                    icon={createColoredIcon(cityData.data.wildfire.risk_category)}
                  >
                    <Popup>
                      <div style={{ minWidth: '200px' }}>
                        <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 'bold' }}>
                          {cityData.data.location}
                        </h3>
                        
                        <div style={{ marginBottom: '10px' }}>
                          <strong>Risk Category:</strong>{' '}
                          <span
                            style={{
                              color:
                                cityData.data.wildfire.risk_category === 'Extreme'
                                  ? '#F44336'
                                  : cityData.data.wildfire.risk_category === 'High'
                                  ? '#FF9800'
                                  : cityData.data.wildfire.risk_category === 'Moderate'
                                  ? '#FFC107'
                                  : '#2196F3',
                              fontWeight: 'bold',
                            }}
                          >
                            {cityData.data.wildfire.risk_category}
                          </span>
                        </div>

                        <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                          <div>
                            <strong>FWI:</strong> {cityData.data.wildfire.FWI.toFixed(2)}
                          </div>
                          <div>
                            <strong>FWI_z:</strong> {cityData.data.wildfire.FWI_z.toFixed(2)}
                          </div>
                          <div>
                            <strong>Ignition Probability:</strong>{' '}
                            {(cityData.data.wildfire.ignition_probability * 100).toFixed(2)}%
                          </div>
                        </div>

                        <hr style={{ margin: '10px 0', border: 'none', borderTop: '1px solid #ddd' }} />

                        <div style={{ fontSize: '13px', lineHeight: '1.5', color: '#666' }}>
                          <div>
                            <strong>Temperature:</strong> {cityData.data.weather.temperature}°F
                          </div>
                          <div>
                            <strong>Humidity:</strong> {cityData.data.weather.humidity}%
                          </div>
                          <div>
                            <strong>Wind Speed:</strong> {cityData.data.weather.windspeed} mph
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>

              {/* Legend */}
              <MDBox mt={3} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <MDTypography variant="button" fontWeight="medium" color="text" mb={1}>
                  Risk Category Legend:
                </MDTypography>
                <MDBox display="flex" flexWrap="wrap" gap={3} mt={1}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#2196F3',
                        border: '2px solid white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      }}
                    ></div>
                    <MDTypography variant="caption" color="text">
                      Low
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#FFC107',
                        border: '2px solid white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      }}
                    ></div>
                    <MDTypography variant="caption" color="text">
                      Moderate
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#FF9800',
                        border: '2px solid white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      }}
                    ></div>
                    <MDTypography variant="caption" color="text">
                      High
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#F44336',
                        border: '2px solid white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      }}
                    ></div>
                    <MDTypography variant="caption" color="text">
                      Extreme
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            </>
          )}
        </MDBox>
      </Card>
    </MDBox>
  );
}

export default WildfireRiskMap;
